import agwaConfirmDialogStyles from './agwa-confirm-dialog.styles.css?inline';
import { LitElement, html, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';

export type ConfirmDialogVariant =
  'default' | 'primary' | 'success' | 'neutral' | 'warning' | 'danger';

export interface ConfirmDialogOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: ConfirmDialogVariant;
  hideCancel?: boolean;
  allowOverlayDismiss?: boolean;
}

const DEFAULT_OPTIONS: Required<Omit<ConfirmDialogOptions, 'variant'>> & {
  variant: ConfirmDialogVariant;
} = {
  title: 'Confirm action',
  message: '',
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  variant: 'primary',
  hideCancel: false,
  allowOverlayDismiss: true,
};

let activeDialog: AgwaConfirmDialog | null = null;

export function confirmAction(options: ConfirmDialogOptions): Promise<boolean> {
  const dialog = ensureDialogInstance();
  return dialog.prompt(options);
}

export type AlertDialogOptions = Omit<ConfirmDialogOptions, 'hideCancel'>;

export function alertAction(options: AlertDialogOptions): Promise<void> {
  return confirmAction({
    ...options,
    confirmText: options.confirmText ?? 'OK',
    hideCancel: true,
    allowOverlayDismiss: options.allowOverlayDismiss ?? true,
  }).then(() => undefined);
}

function ensureDialogInstance() {
  if (activeDialog) {
    return activeDialog;
  }
  activeDialog = document.createElement('agwa-confirm-dialog');
  document.body.appendChild(activeDialog);
  return activeDialog;
}

@customElement('agwa-confirm-dialog')
class AgwaConfirmDialog extends LitElement {
  static styles = unsafeCSS(agwaConfirmDialogStyles);

  @state() private open = false;
  @state() private currentOptions: ConfirmDialogOptions = {
    ...DEFAULT_OPTIONS,
  };

  private resolver: ((value: boolean) => void) | null = null;

  @property({ type: Boolean, reflect: true })
  allowOverlayDismiss = true;

  async prompt(options: ConfirmDialogOptions): Promise<boolean> {
    this.currentOptions = {
      ...DEFAULT_OPTIONS,
      ...options,
    };
    this.allowOverlayDismiss =
      options.allowOverlayDismiss ?? DEFAULT_OPTIONS.allowOverlayDismiss;
    await this.updateComplete;
    requestAnimationFrame(() => {
      this.open = true;
    });
    return new Promise<boolean>((resolve) => {
      this.resolver = resolve;
    });
  }

  private resolve(result: boolean) {
    if (this.resolver) {
      this.resolver(result);
    }
    this.resolver = null;
    this.open = false;
    this.requestUpdate();
  }

  private handleCancel() {
    this.resolve(false);
  }

  private handleConfirm() {
    this.resolve(true);
  }

  private handleAfterHide() {
    if (!this.resolver) {
      this.cleanup();
      return;
    }
    this.resolve(false);
    this.cleanup();
  }

  private handleRequestClose(event: CustomEvent<{ source: string }>) {
    if (!this.allowOverlayDismiss && event.detail.source === 'overlay') {
      event.preventDefault();
      return;
    }
    if (event.detail.source === 'keyboard') {
      this.handleCancel();
    }
  }

  private cleanup() {
    this.resolver = null;
    this.open = false;
    this.requestUpdate();
  }

  render() {
    const { title, message, confirmText, cancelText, variant, hideCancel } =
      this.currentOptions;
    return html`
      <sl-dialog
        ?open=${this.open}
        label=${title ?? DEFAULT_OPTIONS.title}
        @sl-after-hide=${this.handleAfterHide}
        @sl-request-close=${this.handleRequestClose}
      >
        <p>${message}</p>
        <div slot="footer" class="dialog-footer">
          ${
            hideCancel
              ? null
              : html`<sl-button variant="neutral" @click=${this.handleCancel}>
                  ${cancelText ?? DEFAULT_OPTIONS.cancelText}
                </sl-button>`
          }
          <sl-button
            variant=${variant ?? DEFAULT_OPTIONS.variant}
            @click=${this.handleConfirm}
          >
            ${confirmText ?? DEFAULT_OPTIONS.confirmText}
          </sl-button>
        </div>
      </sl-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'agwa-confirm-dialog': AgwaConfirmDialog;
  }
}
